import { useState, useEffect, useRef } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { X, Plus, Star, Trash2, Camera, Upload, Image, ThumbsUp, ThumbsDown, Scissors } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

// Renders a "Before" or "After" photo strip — thumbnail per uploaded
// image with a × remove button, plus a trailing dashed "+ Add"
// tile that opens the multi-select file picker. Lets a stylist photo
// a transformation from a few angles without forcing them to overwrite.
function PhotoSection({ label, photos, inputRef, uploading, onPick, onRemove }) {
  const empty = photos.length === 0;
  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={e => { onPick(e.target.files); e.target.value = ""; }} />
      {empty ? (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid={`upload-${label.toLowerCase()}-photo`}
          className="w-full h-32 border-2 border-dashed border-[#E6E4E0] rounded-lg flex flex-col items-center justify-center gap-1.5 text-[#8A8A8A] hover:border-[#B5A493] hover:text-[#5E5E5E] transition-colors">
          <Camera className="w-5 h-5" />
          <span className="text-xs font-medium">{uploading ? "Uploading..." : `${label} Photos`}</span>
          <span className="text-[10px] text-[#A8A8A8]">Pick one or more</span>
        </button>
      ) : (
        <div className="grid grid-cols-3 gap-1.5" data-testid={`${label.toLowerCase()}-photo-grid`}>
          {photos.map((p, i) => (
            <div key={p.filename || p.url || `photo-${i}`} className="relative rounded-lg overflow-hidden border border-[#E6E4E0] aspect-square">
              <img src={`${BACKEND_URL}${p.url}`} alt={`${label} ${i + 1}`} className="w-full h-full object-cover" />
              <div className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 bg-black/60 text-white rounded">{label} {i + 1}</div>
              <button type="button" onClick={() => onRemove(p.filename)}
                data-testid={`remove-${label.toLowerCase()}-photo-${i}`}
                className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-rose-600">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {/* + Add more tile — same grid cell size so the strip stays even */}
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid={`add-more-${label.toLowerCase()}-photos`}
            className="aspect-square border-2 border-dashed border-[#E6E4E0] rounded-lg flex flex-col items-center justify-center text-[#8A8A8A] hover:border-[#B5A493] hover:text-[#5E5E5E] transition-colors">
            <Plus className="w-4 h-4" />
            <span className="text-[10px] font-medium mt-0.5">{uploading ? "..." : "Add"}</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Create/Edit Session Modal ───
export function CreateSessionModal({ tracks, onClose, onCreated, existing = null }) {
  const isEdit = !!existing;
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(() => existing ? {
    track_id: existing.track_id || "",
    title: existing.title || "",
    session_type: existing.session_type || "classroom",
    date: existing.date || "",
    start_time: existing.start_time || "10:00",
    end_time: existing.end_time || "12:00",
    location_id: existing.location_id || "loc_rochester",
    educator_id: existing.educator_id || "",
    trainee_ids: existing.trainee_ids || [],
    agenda: existing.agenda || "",
    objectives: existing.objectives || [],
    prep_instructions: existing.prep_instructions || "",
    materials_needed: existing.materials_needed || [],
    model_info: existing.model_info || { name: "", hair_type: "", starting_point: "", consent_signed: false },
    status: existing.status || "scheduled",
    notes: existing.notes || "",
  } : {
    track_id: "", title: "", session_type: "classroom", date: "",
    start_time: "10:00", end_time: "12:00", location_id: "loc_rochester",
    educator_id: "", trainee_ids: [], agenda: "", objectives: [],
    prep_instructions: "", materials_needed: [],
    model_info: { name: "", hair_type: "", starting_point: "", consent_signed: false },
  });
  const [objInput, setObjInput] = useState("");
  const [matInput, setMatInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/staff").then(r => setStaff(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.track_id || !form.title || !form.date || !form.educator_id) {
      toast.error("Fill required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.session_type !== "hands_on_model") {
        payload.model_info = {};
      }
      if (isEdit) {
        await api.put(`/training/sessions/${existing.session_id}`, payload);
        toast.success("Session updated");
      } else {
        await api.post("/training/sessions", payload);
        toast.success("Session created");
      }
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Failed to update session" : "Failed to create session"));
    }
    finally { setSaving(false); }
  };

  const toggleTrainee = (uid) => {
    setForm(f => ({
      ...f,
      trainee_ids: f.trainee_ids.includes(uid)
        ? f.trainee_ids.filter(id => id !== uid)
        : [...f.trainee_ids, uid]
    }));
  };

  const addObjective = () => {
    if (objInput.trim()) {
      setForm(f => ({ ...f, objectives: [...f.objectives, objInput.trim()] }));
      setObjInput("");
    }
  };

  const addMaterial = () => {
    if (matInput.trim()) {
      setForm(f => ({ ...f, materials_needed: [...f.materials_needed, matInput.trim()] }));
      setMatInput("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="create-session-modal">
      <div className="w-full max-w-[640px] max-h-[90vh] bg-white rounded-2xl overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-[#E6E4E0] px-6 py-4 flex items-center justify-between">
          <h2 className="font-['Outfit'] text-xl font-medium text-[#1A1A1A]">{isEdit ? "Edit Training Session" : "Schedule Training Session"}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center">
            <X className="w-4 h-4 text-[#5E5E5E]" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Training Track *</label>
              <Select value={form.track_id} onValueChange={v => setForm(f => ({ ...f, track_id: v }))}>
                <SelectTrigger className="border-[#E6E4E0]" data-testid="session-track-select"><SelectValue placeholder="Select track" /></SelectTrigger>
                <SelectContent>
                  {tracks.map(t => <SelectItem key={t.track_id} value={t.track_id}>{t.service_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Session Type</label>
              <Select value={form.session_type} onValueChange={v => setForm(f => ({ ...f, session_type: v }))}>
                <SelectTrigger className="border-[#E6E4E0]" data-testid="session-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["classroom", "demo", "shadow", "hands_on_model", "quality_check", "certification", "remediation"].map(t => (
                    <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Title *</label>
            <Input data-testid="session-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Partial Highlight — Model Day #3" className="border-[#E6E4E0]" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Date *</label>
              <Input data-testid="session-date-input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="border-[#E6E4E0]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Start</label>
              <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="border-[#E6E4E0]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">End</label>
              <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className="border-[#E6E4E0]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Location</label>
              <Select value={form.location_id} onValueChange={v => setForm(f => ({ ...f, location_id: v }))}>
                <SelectTrigger className="border-[#E6E4E0]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="loc_rochester">Rochester</SelectItem>
                  <SelectItem value="loc_webster">Webster</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Educator *</label>
              <Select value={form.educator_id} onValueChange={v => setForm(f => ({ ...f, educator_id: v }))}>
                <SelectTrigger className="border-[#E6E4E0]" data-testid="session-educator-select"><SelectValue placeholder="Select educator" /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Educators are: explicit role="educator" OR anyone with the
                    // is_educator permission flag turned on (any role can opt in).
                    const educators = staff.filter(s => (s.role === "educator" || s.is_educator) && s.is_active !== false);
                    if (educators.length === 0) {
                      return <div className="px-3 py-2 text-xs text-slate-400">No educators yet — mark a team member as Educator on the Staff page first.</div>;
                    }
                    return educators.map(s => (
                      <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Trainees</label>
            <div className="flex flex-wrap gap-2">
              {staff.filter(s => s.role !== "owner").map(s => (
                <button key={s.user_id} type="button" onClick={() => toggleTrainee(s.user_id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    form.trainee_ids.includes(s.user_id) ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E6E4E0] text-[#5E5E5E] hover:border-[#B5A493]"
                  }`}>{s.name}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Agenda</label>
            <Textarea value={form.agenda} onChange={e => setForm(f => ({ ...f, agenda: e.target.value }))} rows={2}
              placeholder="Session agenda..." className="border-[#E6E4E0] text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Objectives</label>
            <div className="flex gap-2 mb-2">
              <Input value={objInput} onChange={e => setObjInput(e.target.value)} placeholder="Add objective" className="border-[#E6E4E0] text-sm"
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addObjective())} />
              <Button type="button" variant="outline" onClick={addObjective} className="border-[#E6E4E0] px-3"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {form.objectives.map((o, i) => (
                <span key={i} className="text-xs px-2 py-1 bg-[#F8F7F5] rounded-lg text-[#5E5E5E] flex items-center gap-1">
                  {o}<button type="button" onClick={() => setForm(f => ({ ...f, objectives: f.objectives.filter((_, j) => j !== i) }))}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>
          {form.session_type === "hands_on_model" && (
            <div className="border border-[#E6E4E0] rounded-lg p-4 space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A]">Model Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <Input value={form.model_info.name} onChange={e => setForm(f => ({ ...f, model_info: { ...f.model_info, name: e.target.value } }))}
                  placeholder="Model name" className="border-[#E6E4E0] text-sm" />
                <Input value={form.model_info.hair_type} onChange={e => setForm(f => ({ ...f, model_info: { ...f.model_info, hair_type: e.target.value } }))}
                  placeholder="Hair type / description" className="border-[#E6E4E0] text-sm" />
              </div>
              <Input value={form.model_info.starting_point} onChange={e => setForm(f => ({ ...f, model_info: { ...f.model_info, starting_point: e.target.value } }))}
                placeholder="Starting point (e.g. Level 5 virgin)" className="border-[#E6E4E0] text-sm" />
              <label className="flex items-center gap-2 text-sm text-[#5E5E5E] cursor-pointer">
                <input type="checkbox" checked={form.model_info.consent_signed}
                  onChange={e => setForm(f => ({ ...f, model_info: { ...f.model_info, consent_signed: e.target.checked } }))}
                  className="rounded border-[#E6E4E0]" />
                Model consent signed
              </label>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#E6E4E0] rounded-full px-5">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="submit-session-btn"
              className="bg-[#1A1A1A] text-white rounded-full px-5 hover:bg-[#333]">{saving ? "Saving..." : (isEdit ? "Save Session" : "Create Session")}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Create/Edit Evaluation Modal ───
export function CreateEvaluationModal({ sessions, tracks, onClose, onCreated, existing = null, prefill = null }) {
  const isEdit = !!existing;
  const [staff, setStaff] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  // Initial form values come from `existing` (edit mode) OR `prefill`
  // (seed when launching evaluation from a session). Plain empty defaults
  // when neither is provided.
  const seed = existing || prefill || {};
  const [form, setForm] = useState(() => ({
    session_id: seed.session_id || "",
    track_id: seed.track_id || "",
    trainee_id: seed.trainee_id || "",
    educator_id: seed.educator_id || "",
    service_name: seed.service_name || "",
    overall_status: seed.overall_status || "pass",
    educator_notes: seed.educator_notes || "",
    formula_notes: seed.formula_notes || "",
    timing_actual_minutes: seed.timing_actual_minutes || 0,
    strengths: seed.strengths || [],
    improvements: seed.improvements || [],
  }));
  const [scores, setScores] = useState(() => existing?.scores || []);
  const [strInput, setStrInput] = useState("");
  const [impInput, setImpInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState(() => existing?.photos || []);
  const [uploading, setUploading] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  useEffect(() => {
    api.get("/staff").then(r => setStaff(r.data)).catch(() => {});
  }, []);

  // Hydrate selectedTrack from existing/prefill so competencies render
  useEffect(() => {
    const seed = existing || prefill;
    if (seed?.track_id && tracks?.length) {
      const t = tracks.find(tr => tr.track_id === seed.track_id);
      if (t) {
        setSelectedTrack(t);
        // For a fresh-from-prefill flow, also seed the score rows from
        // the track's competencies so the educator can start scoring
        // right away. (`existing` mode already has scores in state.)
        if (prefill && !existing && t.competencies?.length) {
          setScores(t.competencies.map(c => ({
            competency: c.name, description: c.description || "",
            score: 3, weight: c.weight, status: "pass", notes: ""
          })));
        }
      }
    }
  }, [existing, prefill, tracks]);

  const handleTrackChange = (trackId) => {
    const track = tracks.find(t => t.track_id === trackId);
    setSelectedTrack(track);
    setForm(f => ({ ...f, track_id: trackId, service_name: track?.service_name || "" }));
    if (track?.competencies) {
      setScores(track.competencies.map(c => ({
        competency: c.name, description: c.description || "",
        score: 3, weight: c.weight, status: "pass", notes: ""
      })));
    }
  };

  const updateScore = (idx, field, value) => {
    setScores(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value, ...(field === "score" ? { status: value >= 3 ? "pass" : value >= 2 ? "needs_work" : "fail" } : {}) } : s));
  };

  const calcOverall = () => {
    if (scores.length === 0) return 0;
    const totalW = scores.reduce((s, c) => s + c.weight, 0);
    return Math.round(scores.reduce((s, c) => s + (c.score / 5) * 100 * c.weight, 0) / Math.max(totalW, 1));
  };

  const handlePhotoUpload = async (files, label) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      // Upload sequentially so a slow connection still shows progress
      // gradually, and one failure doesn't blow away the rest.
      const newPhotos = [];
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        try {
          const res = await api.post("/training/upload-photo", formData, { headers: { "Content-Type": "multipart/form-data" } });
          newPhotos.push({ url: res.data.url, label, filename: res.data.filename });
        } catch {
          toast.error(`Couldn't upload ${file.name}`);
        }
      }
      if (newPhotos.length) {
        setPhotos(prev => [...prev, ...newPhotos]);
        toast.success(`${newPhotos.length} ${label.toLowerCase()} photo${newPhotos.length > 1 ? "s" : ""} added`);
      }
    } finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.track_id || !form.trainee_id || !form.educator_id) {
      toast.error("Fill required fields");
      return;
    }
    setSaving(true);
    try {
      const overall = calcOverall();
      const hasFailures = scores.some(s => s.status === "fail");
      const hasNeedsWork = scores.some(s => s.status === "needs_work");
      const payload = {
        ...form, scores,
        overall_score: overall,
        overall_status: hasFailures ? "fail" : hasNeedsWork ? "needs_work" : "pass",
      };
      let evalId;
      if (isEdit) {
        await api.put(`/training/evaluations/${existing.eval_id}`, payload);
        evalId = existing.eval_id;
        toast.success("Evaluation updated");
      } else {
        const evalRes = await api.post("/training/evaluations", payload);
        evalId = evalRes.data?.eval_id;
        toast.success("Evaluation submitted");
      }
      // Attach/refresh photos if any
      if (photos.length > 0 && evalId) {
        await api.put(`/training/evaluations/${evalId}/photos`, { photos });
      }
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Failed to update" : "Failed to submit"));
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="create-evaluation-modal">
      <div className="w-full max-w-[700px] max-h-[90vh] bg-white rounded-2xl overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-[#E6E4E0] px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-['Outfit'] text-xl font-medium text-[#1A1A1A]">{isEdit ? "Edit Evaluation" : "Training Scorecard"}</h2>
            {scores.length > 0 && <p className="text-xs text-[#8A8A8A]">Overall: {calcOverall()}/100</p>}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center">
            <X className="w-4 h-4 text-[#5E5E5E]" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Training Track *</label>
              <Select value={form.track_id} onValueChange={handleTrackChange}>
                <SelectTrigger className="border-[#E6E4E0]" data-testid="eval-track-select"><SelectValue placeholder="Select track" /></SelectTrigger>
                <SelectContent>
                  {tracks.map(t => <SelectItem key={t.track_id} value={t.track_id}>{t.service_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Session</label>
              <Select value={form.session_id} onValueChange={v => setForm(f => ({ ...f, session_id: v }))}>
                <SelectTrigger className="border-[#E6E4E0]"><SelectValue placeholder="Link to session (optional)" /></SelectTrigger>
                <SelectContent>
                  {sessions.filter(s => s.track_id === form.track_id || !form.track_id).map(s => (
                    <SelectItem key={s.session_id} value={s.session_id}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Trainee *</label>
              <Select value={form.trainee_id} onValueChange={v => setForm(f => ({ ...f, trainee_id: v }))}>
                <SelectTrigger className="border-[#E6E4E0]" data-testid="eval-trainee-select"><SelectValue placeholder="Select trainee" /></SelectTrigger>
                <SelectContent>
                  {staff.filter(s => s.role !== "owner" && s.role !== "front_desk").map(s => (
                    <SelectItem key={s.user_id} value={s.user_id}>{s.name} — {s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Educator *</label>
              <Select value={form.educator_id} onValueChange={v => setForm(f => ({ ...f, educator_id: v }))}>
                <SelectTrigger className="border-[#E6E4E0]" data-testid="eval-educator-select"><SelectValue placeholder="Select educator" /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    const educators = staff.filter(s => (s.role === "educator" || s.is_educator) && s.is_active !== false);
                    if (educators.length === 0) {
                      return <div className="px-3 py-2 text-xs text-slate-400">No educators yet — mark a team member as Educator on the Staff page first.</div>;
                    }
                    return educators.map(s => (
                      <SelectItem key={s.user_id} value={s.user_id}>
                        {s.name}{s.title ? ` — ${s.title}` : s.role ? ` — ${s.role.replace(/_/g, " ")}` : ""}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Scorecard */}
          {scores.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-3">Competency Scores</h4>
              <div className="space-y-3">
                {scores.map((s, i) => (
                  <div key={i} className="border border-[#E6E4E0] rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-[#1A1A1A]" data-testid={`eval-score-title-${i}`}>{s.competency}</p>
                      <span className="text-xs text-[#B5A493]">{s.weight}%</span>
                    </div>
                    {s.description && (
                      <p className="text-[11px] text-slate-500 leading-relaxed mb-2 font-['Manrope']" data-testid={`eval-score-description-${i}`}>
                        {s.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(star => (
                          <button key={star} type="button" onClick={() => updateScore(i, "score", star)}
                            className="transition-transform hover:scale-110">
                            <Star className="w-5 h-5" fill={star <= s.score ? "#F59E0B" : "none"} stroke={star <= s.score ? "#F59E0B" : "#E6E4E0"} />
                          </button>
                        ))}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        s.status === "pass" ? "bg-[#E8F0E9] text-[#2D5936]" :
                        s.status === "fail" ? "bg-[#FBEAE9] text-[#8A2A23]" :
                        "bg-[#FDF3E1] text-[#805B10]"
                      }`}>{s.status === "pass" ? "Pass" : s.status === "fail" ? "Fail" : "Needs Work"}</span>
                    </div>
                    <Input value={s.notes} onChange={e => updateScore(i, "notes", e.target.value)}
                      placeholder="Notes..." className="border-[#E6E4E0] text-xs h-8" />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Actual Timing (minutes)</label>
            <Input type="number" value={form.timing_actual_minutes || ""} onChange={e => setForm(f => ({ ...f, timing_actual_minutes: parseInt(e.target.value) || 0 }))}
              placeholder="e.g. 130" className="border-[#E6E4E0] w-32" />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Educator Notes</label>
            <Textarea value={form.educator_notes} onChange={e => setForm(f => ({ ...f, educator_notes: e.target.value }))} rows={3}
              placeholder="Overall observations, recommendations..." className="border-[#E6E4E0] text-sm" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#2D5936] mb-1.5 block">Strengths</label>
              <div className="flex gap-2 mb-1">
                <Input value={strInput} onChange={e => setStrInput(e.target.value)} placeholder="Add strength" className="border-[#E6E4E0] text-xs h-8"
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (strInput.trim()) { setForm(f => ({ ...f, strengths: [...f.strengths, strInput.trim()] })); setStrInput(""); } } }} />
                <Button type="button" variant="outline" size="sm" className="h-8 px-2 border-[#E6E4E0]"
                  onClick={() => { if (strInput.trim()) { setForm(f => ({ ...f, strengths: [...f.strengths, strInput.trim()] })); setStrInput(""); } }}><Plus className="w-3 h-3" /></Button>
              </div>
              {form.strengths.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#E8F0E9] text-[#2D5936] rounded mr-1 mb-1">
                  {s}<button type="button" onClick={() => setForm(f => ({ ...f, strengths: f.strengths.filter((_, j) => j !== i) }))}><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#805B10] mb-1.5 block">Improvements</label>
              <div className="flex gap-2 mb-1">
                <Input value={impInput} onChange={e => setImpInput(e.target.value)} placeholder="Add area" className="border-[#E6E4E0] text-xs h-8"
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (impInput.trim()) { setForm(f => ({ ...f, improvements: [...f.improvements, impInput.trim()] })); setImpInput(""); } } }} />
                <Button type="button" variant="outline" size="sm" className="h-8 px-2 border-[#E6E4E0]"
                  onClick={() => { if (impInput.trim()) { setForm(f => ({ ...f, improvements: [...f.improvements, impInput.trim()] })); setImpInput(""); } }}><Plus className="w-3 h-3" /></Button>
              </div>
              {form.improvements.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#FDF3E1] text-[#805B10] rounded mr-1 mb-1">
                  {s}<button type="button" onClick={() => setForm(f => ({ ...f, improvements: f.improvements.filter((_, j) => j !== i) }))}><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Formula Notes</label>
            <Input value={form.formula_notes} onChange={e => setForm(f => ({ ...f, formula_notes: e.target.value }))}
              placeholder="e.g. 30g lightener + 30ml 20-vol developer" className="border-[#E6E4E0] text-sm" />
          </div>

          {/* Before/After Photos — multi-upload. Each tile is removable;
              the trailing dashed tile opens the file picker (multi-select
              enabled) so staff can add a whole set in one tap. */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-2 block">Before & After Photos</label>
            <div className="grid grid-cols-2 gap-4">
              <PhotoSection label="Before" photos={photos.filter(p => p.label === "Before")}
                inputRef={beforeRef} uploading={uploading}
                onPick={(files) => handlePhotoUpload(files, "Before")}
                onRemove={(filename) => setPhotos(prev => prev.filter(p => !(p.label === "Before" && p.filename === filename)))} />
              <PhotoSection label="After" photos={photos.filter(p => p.label === "After")}
                inputRef={afterRef} uploading={uploading}
                onPick={(files) => handlePhotoUpload(files, "After")}
                onRemove={(filename) => setPhotos(prev => prev.filter(p => !(p.label === "After" && p.filename === filename)))} />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#E6E4E0] rounded-full px-5">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="submit-evaluation-btn"
              className="bg-[#1A1A1A] text-white rounded-full px-5 hover:bg-[#333]">{saving ? "Submitting..." : (isEdit ? "Save Evaluation" : "Submit Evaluation")}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Create Track Modal ───
export function CreateTrackModal({ onClose, onCreated, existing = null }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(() => existing ? {
    service_name: existing.service_name || "",
    service_category: existing.service_category || "Hair",
    description: existing.description || "",
    skill_level: existing.skill_level || "intermediate",
    prerequisites: existing.prerequisites || [],
    time_standard_minutes: existing.time_standard_minutes || 60,
    quality_standards: existing.quality_standards || "",
    consultation_standards: existing.consultation_standards || "",
    finishing_standards: existing.finishing_standards || "",
    retail_expectations: existing.retail_expectations || "",
    educator_notes: existing.educator_notes || "",
    approval_requirements: existing.approval_requirements || { min_model_sessions: 3, min_score: 75, educator_signoff: true, manager_signoff: false, required_shadow_sessions: 2 },
  } : {
    service_name: "", service_category: "Hair", description: "", skill_level: "intermediate",
    prerequisites: [], time_standard_minutes: 60,
    quality_standards: "", consultation_standards: "", finishing_standards: "",
    retail_expectations: "", educator_notes: "",
    approval_requirements: { min_model_sessions: 3, min_score: 75, educator_signoff: true, manager_signoff: false, required_shadow_sessions: 2 },
  });
  const [competencies, setCompetencies] = useState(() =>
    existing?.competencies?.length ? existing.competencies : [{ name: "", weight: 10, description: "" }]
  );
  const [prereqInput, setPrereqInput] = useState("");
  const [saving, setSaving] = useState(false);

  const addCompetency = () => setCompetencies(p => [...p, { name: "", weight: 10, description: "" }]);
  const removeCompetency = (i) => setCompetencies(p => p.filter((_, j) => j !== i));
  const updateCompetency = (i, field, value) => setCompetencies(p => p.map((c, j) => j === i ? { ...c, [field]: value } : c));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.service_name) { toast.error("Service name required"); return; }
    const validComps = competencies.filter(c => c.name.trim());
    if (validComps.length === 0) { toast.error("Add at least one competency"); return; }
    setSaving(true);
    try {
      const payload = { ...form, competencies: validComps };
      if (isEdit) {
        await api.put(`/training/tracks/${existing.track_id}`, payload);
        toast.success("Training track updated");
      } else {
        await api.post("/training/tracks", payload);
        toast.success("Training track created");
      }
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Failed to update track" : "Failed to create track"));
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="create-track-modal">
      <div className="w-full max-w-[640px] max-h-[90vh] bg-white rounded-2xl overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-[#E6E4E0] px-6 py-4 flex items-center justify-between">
          <h2 className="font-['Outfit'] text-xl font-medium text-[#1A1A1A]">{isEdit ? "Edit Training Track" : "Create Training Track"}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center">
            <X className="w-4 h-4 text-[#5E5E5E]" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Service Name *</label>
              <Input data-testid="track-name-input" value={form.service_name} onChange={e => setForm(f => ({ ...f, service_name: e.target.value }))}
                placeholder="e.g. Full Highlight" className="border-[#E6E4E0]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Category</label>
              <Select value={form.service_category} onValueChange={v => setForm(f => ({ ...f, service_category: v }))}>
                <SelectTrigger className="border-[#E6E4E0]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Hair", "Color", "Lashes & Brows", "Skin", "Nails"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Skill Level</label>
              <Select value={form.skill_level} onValueChange={v => setForm(f => ({ ...f, skill_level: v }))}>
                <SelectTrigger className="border-[#E6E4E0]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="junior">Junior</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Time Standard (min)</label>
              <Input type="number" value={form.time_standard_minutes} onChange={e => setForm(f => ({ ...f, time_standard_minutes: parseInt(e.target.value) || 0 }))}
                className="border-[#E6E4E0]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Description</label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
              placeholder="Describe the service training focus..." className="border-[#E6E4E0] text-sm" />
          </div>

          {/* Competencies */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A]">Competencies</label>
              <Button type="button" variant="outline" size="sm" onClick={addCompetency} className="h-7 text-xs border-[#E6E4E0] rounded-full px-3" data-testid="add-competency-btn">
                <Plus className="w-3 h-3 mr-1" />Add
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 -mt-1 mb-2 font-['Manrope']">
              Title is what shows on the scorecard. Description gives educators the rubric for what "pass" looks like.
            </p>
            <div className="space-y-3">
              {competencies.map((c, i) => (
                <div key={i} className="border border-[#E6E4E0] rounded-xl p-3 bg-[#FAFAFA]" data-testid={`competency-row-${i}`}>
                  <div className="flex gap-2 items-start">
                    <div className="flex-1 grid grid-cols-[1fr_72px] gap-2">
                      <Input value={c.name} onChange={e => updateCompetency(i, "name", e.target.value)}
                        placeholder="Competency title (e.g. Sectioning)"
                        className="border-[#E6E4E0] text-sm h-9 bg-white"
                        data-testid={`competency-name-${i}`} />
                      <div className="relative">
                        <Input type="number" value={c.weight} onChange={e => updateCompetency(i, "weight", parseInt(e.target.value) || 0)}
                          placeholder="%" className="border-[#E6E4E0] text-sm h-9 text-center pr-5 bg-white"
                          data-testid={`competency-weight-${i}`} />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">%</span>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeCompetency(i)}
                      data-testid={`remove-competency-${i}`}
                      aria-label="Remove competency"
                      className="mt-1.5 text-[#8A8A8A] hover:text-[#8A2A23] transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <Textarea
                    value={c.description || ""}
                    onChange={e => updateCompetency(i, "description", e.target.value)}
                    placeholder="What does passing this look like? (e.g. Clean, even partings with no straggling hair.)"
                    rows={2}
                    className="mt-2 border-[#E6E4E0] text-sm bg-white"
                    data-testid={`competency-description-${i}`} />
                </div>
              ))}
              {competencies.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-3 border border-dashed border-[#E6E4E0] rounded-xl">No competencies yet — click Add.</p>
              )}
            </div>
            {(() => {
              const total = competencies.reduce((s, c) => s + (parseInt(c.weight) || 0), 0);
              if (total === 0) return null;
              const pct = total;
              const off = total !== 100;
              return (
                <p className={`text-[11px] mt-2 font-['Manrope'] ${off ? "text-amber-600" : "text-emerald-600"}`} data-testid="competency-weight-total">
                  Total weight: <span className="font-semibold">{pct}%</span>
                  {off && <span className="ml-1">— should total 100% for a balanced scorecard.</span>}
                </p>
              );
            })()}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Quality Standards</label>
            <Textarea value={form.quality_standards} onChange={e => setForm(f => ({ ...f, quality_standards: e.target.value }))} rows={2}
              placeholder="Expected quality outcomes..." className="border-[#E6E4E0] text-sm" />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#E6E4E0] rounded-full px-5">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="submit-track-btn"
              className="bg-[#1A1A1A] text-white rounded-full px-5 hover:bg-[#333]">{saving ? (isEdit ? "Saving..." : "Creating...") : (isEdit ? "Save Track" : "Create Track")}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ─── Create Resource Modal ───
export function CreateResourceModal({ tracks, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", type: "guide", description: "", track_ids: [], url: "",
  });
  const [saving, setSaving] = useState(false);

  const toggleTrack = (tid) => {
    setForm(f => ({
      ...f,
      track_ids: f.track_ids.includes(tid) ? f.track_ids.filter(id => id !== tid) : [...f.track_ids, tid]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.post("/training/resources", form);
      toast.success("Resource added to library");
      onCreated();
    } catch { toast.error("Failed to create resource"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="create-resource-modal">
      <div className="w-full max-w-[560px] max-h-[90vh] bg-white rounded-2xl overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-[#E6E4E0] px-6 py-4 flex items-center justify-between">
          <h2 className="font-['Outfit'] text-xl font-medium text-[#1A1A1A]">Add to Library</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center">
            <X className="w-4 h-4 text-[#5E5E5E]" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Title *</label>
            <Input data-testid="resource-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Balayage Saturation Guide" className="border-[#E6E4E0]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Type</label>
              <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger className="border-[#E6E4E0]" data-testid="resource-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["guide", "sop", "reference", "video", "script", "photo_reference"].map(t => (
                    <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">URL (optional)</label>
              <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                placeholder="https://..." className="border-[#E6E4E0]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Description</label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3}
              placeholder="Describe this resource..." className="border-[#E6E4E0] text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#8A8A8A] mb-1.5 block">Linked Tracks</label>
            <div className="flex flex-wrap gap-2">
              {(tracks || []).map(t => (
                <button key={t.track_id} type="button" onClick={() => toggleTrack(t.track_id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    form.track_ids.includes(t.track_id) ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E6E4E0] text-[#5E5E5E] hover:border-[#B5A493]"
                  }`}>{t.service_name}</button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#E6E4E0] rounded-full px-5">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="submit-resource-btn"
              className="bg-[#1A1A1A] text-white rounded-full px-5 hover:bg-[#333]">{saving ? "Adding..." : "Add Resource"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ─── Quick Haircut Check Modal ───
// Light-touch sanity check an educator runs in <30 seconds after a
// stylist's haircut. Different from full evaluation: no track, no
// weighted competencies, no photos — just 5 thumbs + notes.
const QHC_QUESTIONS = {
  womens: [
    "Are the sides even?",
    "Is the face frame even?",
    "Are the layers even and textured/blended (if requested)?",
    "Is the back of the haircut clean when the guest looks down?",
    "Is the blow-dry complete and professional?",
  ],
  mens: [
    "Are the sides even and blended?",
    "Is the hairline cleaned up with no hair down the neck and around the ears?",
    "Is the top even and cross-checked?",
    "Is the cut styled and finished?",
    "Is the finish polished (product, blow-dry, or style applied)?",
  ],
};

export function QuickHaircutCheckModal({ onClose, onCreated }) {
  const [staff, setStaff] = useState([]);
  const [stylistId, setStylistId] = useState("");
  const [cutType, setCutType] = useState("womens");
  // answers: array of bools per question index. Defaults to undefined
  // (neither up nor down) so we can require the educator answers all 5
  // before submitting.
  const [answers, setAnswers] = useState(() => Array(5).fill(null));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Pull only stylist-ish roles for the dropdown — front desk and
    // support staff don't do haircuts.
    api.get("/staff").then(r => {
      const all = r.data || [];
      const cuts_roles = new Set(["stylist", "colorist", "barber", "master_stylist", "senior_stylist", "junior_stylist", "apprentice"]);
      const filtered = all.filter(s => !s.role || cuts_roles.has(s.role) || (s.role || "").includes("stylist") || (s.role || "").includes("barber"));
      setStaff(filtered.length ? filtered : all);
    }).catch(() => {});
  }, []);

  // Reset answers when switching cut types — the questions change, so
  // existing answers don't map cleanly to the new prompts.
  useEffect(() => { setAnswers(Array(5).fill(null)); }, [cutType]);

  const questions = QHC_QUESTIONS[cutType];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stylistId) { toast.error("Pick a stylist"); return; }
    if (answers.some(a => a === null)) { toast.error("Answer all 5 questions before saving"); return; }
    const stylist = staff.find(s => s.user_id === stylistId);
    setSaving(true);
    try {
      await api.post("/training/quick-haircut-checks", {
        stylist_id: stylistId,
        stylist_name: stylist?.name || "",
        cut_type: cutType,
        answers: questions.map((q, i) => ({ question: q, thumbs_up: answers[i] })),
        notes: notes.trim(),
      });
      toast.success("Quick check saved");
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const upCount = answers.filter(a => a === true).length;
  const downCount = answers.filter(a => a === false).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="quick-haircut-check-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center">
              <Scissors className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Quick Haircut Check</h2>
              <p className="text-[11px] text-slate-500">~30 second floor check</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="qhc-close-btn"
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Stylist picker */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Stylist being checked</label>
            <Select value={stylistId} onValueChange={setStylistId}>
              <SelectTrigger className="mt-1.5 h-11 border-slate-200" data-testid="qhc-stylist-select">
                <SelectValue placeholder="Pick a stylist..." />
              </SelectTrigger>
              <SelectContent>
                {staff.map(s => (
                  <SelectItem key={s.user_id} value={s.user_id}>
                    {s.name} {s.title ? `— ${s.title}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Cut type toggle */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Cut type</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {[
                { key: "womens", label: "Women's cut" },
                { key: "mens", label: "Men's cut" },
              ].map(opt => (
                <button key={opt.key} type="button" onClick={() => setCutType(opt.key)}
                  data-testid={`qhc-cuttype-${opt.key}`}
                  className={`h-11 rounded-xl border-2 text-sm font-medium transition-colors ${
                    cutType === opt.key
                      ? "border-rose-500 bg-rose-50 text-rose-700"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 5 thumbs questions */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Quality checks</label>
              <span className="text-[11px] text-slate-500">
                <span className="text-emerald-600 font-semibold">👍 {upCount}</span>
                <span className="mx-1.5">·</span>
                <span className="text-rose-600 font-semibold">👎 {downCount}</span>
                <span className="mx-1.5">·</span>
                <span>{answers.filter(a => a !== null).length}/5</span>
              </span>
            </div>
            {questions.map((q, i) => (
              <div key={i} className="border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3"
                data-testid={`qhc-question-${i}`}>
                <p className="text-sm text-slate-800 flex-1">{q}</p>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button type="button" onClick={() => setAnswers(prev => prev.map((a, idx) => idx === i ? true : a))}
                    data-testid={`qhc-thumbs-up-${i}`}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                      answers[i] === true
                        ? "bg-emerald-500 text-white shadow-sm ring-2 ring-emerald-200"
                        : "bg-slate-100 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"
                    }`} aria-label="Thumbs up">
                    <ThumbsUp className="w-5 h-5" strokeWidth={2} />
                  </button>
                  <button type="button" onClick={() => setAnswers(prev => prev.map((a, idx) => idx === i ? false : a))}
                    data-testid={`qhc-thumbs-down-${i}`}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                      answers[i] === false
                        ? "bg-rose-500 text-white shadow-sm ring-2 ring-rose-200"
                        : "bg-slate-100 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                    }`} aria-label="Thumbs down">
                    <ThumbsDown className="w-5 h-5" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="Anything to coach on, follow up, or call out..."
              data-testid="qhc-notes" className="mt-1.5 resize-none border-slate-200" />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#E6E4E0] rounded-full px-5">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="qhc-submit-btn"
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-5">
              {saving ? "Saving..." : "Save check"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
